import React, { Component } from 'react';
import { Nav } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import * as Cookies from 'es-cookie';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import * as compose from 'lodash.flowright';
import './_student_profile.scss';

import OverviewTab from './tabs/OverviewTab';
import PerformanceTab from './tabs/PerformanceTab';
import SubjectsTopicsTab from './tabs/SubjectsTab';
import TimeAnalysisTab from './tabs/TimeAnalysisTab'; 
import ComplexityTab from './tabs/ComplexityTab';
import PredictionRankTab from './tabs/PredictionRankTab';
import RevisionPlannerTab from './tabs/RevisionPlannerTab';
import DoubtsTab from './tabs/DoubtsTab';
import AtRiskTab from './tabs/AtRiskTab';
import AttendanceTab from './tabs/AttendanceTab';
import FeesTab from './tabs/FeesTab';
import DocumentsTab from './tabs/DocumentsTab';
import { PageShimmer } from './tabs/Shimmer';

// Tab groups mirror the client MOU structure so each requirement is easy to
// find: "Individual Analysis" (MOU 1.1 per-student points), "Batch Analysis"
// (MOU class/branch/batch points), and "Records" (non-analytics profile data).
const GROUP = {
    INDIVIDUAL: 'Individual Analysis',
    BATCH: 'Batch Analysis',
    RECORDS: 'Records',
};

// Tab = nav label + rendered component + the MOU requirement it satisfies.
// `mou` is shown as the section subtitle so the mapping to the MOU is explicit.
const TABS = [
    {
        key: 'overview', label: 'Overview', group: GROUP.INDIVIDUAL, Component: OverviewTab,
        mou: 'Snapshot — score trend vs batch, attempt rate & predicted score at a glance',
    },
    {
        key: 'performance', label: 'Marks Trend', group: GROUP.INDIVIDUAL, Component: PerformanceTab,
        mou: 'Subject-wise marks trend across tests, vs branch average, with speed-vs-accuracy',
    },
    {
        key: 'subjects', label: 'Chapter & Topic Accuracy', group: GROUP.INDIVIDUAL, Component: SubjectsTopicsTab,
        mou: 'Chapter-wise & topic-wise accuracy — pinpoints weak areas at a granular level',
    },
    {
        key: 'time', label: 'Time per Question', group: GROUP.INDIVIDUAL, Component: TimeAnalysisTab,
        mou: 'Time-per-question analysis — speed vs. accuracy tradeoffs, attempted vs. skipped',
    },
    {
        key: 'complexity', label: 'Complexity', group: GROUP.INDIVIDUAL, Component: ComplexityTab,
        mou: 'Performance across question difficulty levels',
    },
    {
        key: 'prediction', label: 'Prediction', group: GROUP.INDIVIDUAL, Component: PredictionRankTab,
        mou: 'Percentile standing & predicted NEET/JEE score vs cutoff',
    },
    {
        key: 'revision', label: 'Revision Planner', group: GROUP.INDIVIDUAL, Component: RevisionPlannerTab,
        mou: 'Auto-generated revision plan built from weakest topic scores',
    },
    {
        key: 'doubts', label: 'Doubts', group: GROUP.INDIVIDUAL, Component: DoubtsTab,
        mou: 'Doubt resolution history — raised, resolved & pending doubts flagged',
    },
    // { key: 'atrisk', label: 'At-Risk', group: GROUP.INDIVIDUAL, Component: AtRiskTab },
    {
        key: 'attendance', label: 'Attendance', group: GROUP.RECORDS, Component: AttendanceTab,
        mou: 'Attendance record',
    },
    {
        key: 'fees', label: 'Fees', group: GROUP.RECORDS, Component: FeesTab,
        mou: 'Fee payment record',
    },
    {
        key: 'documents', label: 'Documents', group: GROUP.RECORDS, Component: DocumentsTab,
        mou: 'Student documents',
    },
];

// Group order for the tab bar (defines the labeled clusters left→right).
const GROUP_ORDER = [GROUP.INDIVIDUAL, GROUP.BATCH, GROUP.RECORDS];

 
const initial = (name) => (name ? name.trim().charAt(0).toUpperCase() : 'S');

const BASE_PATH = '/student/profile';

// Exam options come solely from getStudentExamStreams, split college-style:
// JEE Mains / JEE Advanced (by sub type), one "Quick Exam" option, EAMCET MPC /
// BIPC (AP+TS merged) and every other board under its own name. There is
// intentionally NO hardcoded fallback list: options are data.
//
// An option's identity is its `key` ("2:1:r", "q", "3:0:r,6:0:r", ...) — the only
// unique value; `stream` is the paper sub type and repeats across boards. The
// switcher is SINGLE-select (one exam at a time, as on the college side) and
// stores the selected key in `selectedKeys`. Tabs receive `examStreams` — the
// board ids (neetjee.exam.id) of the option's `members` — which they send as the
// `exam_streams` GraphQL variable, plus `examKeys` to narrow further client-side.

const GET_STUDENT_EXAM_STREAMS = gql`
    query ($mobile: String) {
        getStudentExamStreams(mobile: $mobile) {
            stream
            exam_id
            key
            label
            members
            is_quick_exam
            total_exams
            completed
        }
    }
`;

// Option keys contain commas themselves, so the URL list is '|'-separated.
const KEY_SEP = '|';

// Read the selection off the URL so it survives tab navigation and is
// deep-linkable: ?exam=<key>|<key>. A legacy ?stream=3,2 (board ids) still
// parses; fetchStreams maps each id to the options whose members include it.
const readSelectionFromUrl = (location) => {
    const params = new URLSearchParams((location && location.search) || '');
    const keys = String(params.get('exam') || '')
        .split(KEY_SEP)
        .map((s) => s.trim())
        .filter(Boolean);
    const legacyIds = String(params.get('stream') || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isInteger(n) && n > 0);
    return { keys, legacyIds };
};

// Stable identity for a selection, so a URL write only happens on a real change.
const selectionKey = (keys) => (Array.isArray(keys) ? keys : []).join(KEY_SEP);

// Board ids (neetjee.exam.id) covered by the selected options, de-duped and
// sorted — what the tabs send as exam_streams.
const boardIdsFor = (options, keys) => {
    const ids = [];
    options
        .filter((o) => keys.includes(o.value))
        .forEach((o) => o.members.forEach((m) => { if (!ids.includes(m)) ids.push(m); }));
    return ids.sort((a, b) => a - b);
};

// Resolve the mobile the reports key on (logged-in student, or a passed one).
const resolveMobile = (student) =>
    Cookies.get('mobile') || (student && (student.mobile || student.student_mobile || student.contact_no)) || null;

class StudentProfileTabs extends Component {
    constructor(props) {
        super(props);
        const { keys, legacyIds } = readSelectionFromUrl(props.location);
        this.state = {
            // Selected option keys (see GET_STUDENT_EXAM_STREAMS).
            selectedKeys: keys,
            // Legacy ?stream= board ids, resolved to keys once options load.
            legacyStreamIds: legacyIds,
            // Board ids of the selection, passed to every tab.
            examStreams: [],
            // Start EMPTY — the dropdown renders only from REAL boards returned by
            // getStudentExamStreams. Never a hardcoded fallback list (a student with no
            // exams, or a single board, must not see options they have no data for).
            streamOptions: [],
            streamsLoaded: false,
            streamMenuOpen: false,
        };
    }

    componentDidMount() {
        this.fetchStreams();
        document.addEventListener('mousedown', this.handleOutsideClick);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleOutsideClick);
    }

    // Close the stream dropdown when clicking outside it.
    handleOutsideClick = (e) => {
        if (this.state.streamMenuOpen && this.streamRef && !this.streamRef.contains(e.target)) {
            this.setState({ streamMenuOpen: false });
        }
    };

    toggleStreamMenu = () => this.setState((s) => ({ streamMenuOpen: !s.streamMenuOpen }));

    // Pull the exam boards the student actually has exams in and build the switcher
    // options from them. No allow-list: whatever getStudentExamStreams returns is
    // offered, so a new board needs no front-end change to appear here.
    fetchStreams = async () => {
        const mobile = resolveMobile(this.props.student);
        if (!mobile || !this.props.client) {
            this.setState({ streamsLoaded: true });
            return;
        }
        try {
            const res = await this.props.client.query({
                query: GET_STUDENT_EXAM_STREAMS,
                variables: { mobile: String(mobile) },
                fetchPolicy: 'cache-first', // don't re-fetch on every tab switch
            });
            const rows = (res && res.data && res.data.getStudentExamStreams) || [];
            const options = rows
                // Do NOT filter on stream > 0: EAMCET is sub type 0 and Quick Exam
                // is -1. An option needs a key, a label and at least one exam.
                .filter((r) => r.key && r.label && Number(r.total_exams) > 0)
                .map((r) => {
                    // Every board id this option covers (EAMCET MPC = [3, 6]). Fall
                    // back to exam_id when an older backend sends no members.
                    let members = (r.members || []).map(Number).filter((n) => n > 0);
                    if (!members.length && Number(r.exam_id) > 0) members = [Number(r.exam_id)];
                    return {
                        value: String(r.key),
                        label: r.label,
                        count: Number(r.total_exams) || 0,
                        isQuickExam: !!r.is_quick_exam,
                        members,
                    };
                })
                // An option that maps to no board can't filter any report.
                .filter((o) => o.members.length > 0);

            if (options.length === 0) {
                this.setState({ streamsLoaded: true });
                return;
            }

            // Reconcile the URL selection against the options this student really
            // has: keep the first key that still exists, else map a legacy
            // ?stream= board id to the first non-Quick-Exam option covering it.
            // Nothing matched snaps to the busiest option (rows are ordered by
            // exam count) — no selection would show nothing.
            const fromKey = options.find((o) => this.state.selectedKeys.includes(o.value));
            const fromLegacy = options.find((o) => !o.isQuickExam
                && this.state.legacyStreamIds.some((id) => o.members.includes(id)));
            const selectedKeys = [(fromKey || fromLegacy || options[0]).value];

            if (selectionKey(selectedKeys) !== selectionKey(this.state.selectedKeys)
                || this.state.legacyStreamIds.length) {
                this.syncSelectionToUrl(selectedKeys);
            }
            this.setState({
                streamOptions: options,
                selectedKeys,
                legacyStreamIds: [],
                examStreams: boardIdsFor(options, selectedKeys),
                streamsLoaded: true,
            });
        } catch (e) {
            console.log(e);
            this.setState({ streamsLoaded: true });
        }
    };

    // Write the selected option keys onto the URL query (?exam=<key>|<key>)
    // without dropping the tab/route state. The legacy ?stream= is removed once
    // it has been translated.
    syncSelectionToUrl = (selectedKeys) => {
        if (!this.props.history || !this.props.location) return;
        const params = new URLSearchParams(this.props.location.search);
        params.set('exam', selectedKeys.join(KEY_SEP));
        params.delete('stream');
        this.props.history.replace({
            pathname: this.props.location.pathname,
            search: `?${params.toString()}`,
            state: this.props.location.state,
        });
    };

    // Select one exam (single-select) and close the menu.
    selectStream = (value) => {
        const selectedKeys = [value];
        const examStreams = boardIdsFor(this.state.streamOptions, selectedKeys);
        // Keep the same array when the boards didn't change (JEE Mains -> JEE
        // Advanced are both board 2); tabs watch examKeys for that switch.
        this.setState((s) => ({
            selectedKeys,
            examStreams: s.examStreams.join(',') === examStreams.join(',') ? s.examStreams : examStreams,
            streamMenuOpen: false,
        }));
        this.syncSelectionToUrl(selectedKeys);
    };

    // The active tab is driven by the URL (last path segment), so each tab is a
    // distinct route — deep-linking and the browser back/forward buttons work.
    getActiveTab = () => {
        const { location } = this.props;
        const segment = location && location.pathname
            ? location.pathname.split('/').filter(Boolean).pop()
            : '';
        return TABS.some((t) => t.key === segment) ? segment : 'overview';
    };

    setTab = (key) => {
        if (this.props.history && key !== this.getActiveTab()) {
            // Carry any route state (e.g. the passed student) across tab changes.
            this.props.history.push({
                pathname: `${BASE_PATH}/${key}`,
                state: this.props.location && this.props.location.state,
            });
        }
    };

    goBack = () => {
        if (this.props.history) {
            this.props.history.goBack();
        }
    };

    render() {
        const student = {...(this.props.student || {}) };
        const activeTab = this.getActiveTab();
        const current = TABS.find((t) => t.key === activeTab) || TABS[0];
        const ActiveTab = current.Component;
        const selected = this.state.streamOptions.find((o) => this.state.selectedKeys.includes(o.value));
        const selectedLabel = selected ? selected.label : "Select exam";
        const selectedCount = selected ? selected.count || 0 : 0;

        return (
            <div className="student-profile-page">
                {/* ---- Header / identity card ---- */}
                <div className="sp-header-card">
                    <div className="sp-identity">
                        <div className="sp-avatar">{initial(student.name)}</div>
                        <div>
                            <h4 className="sp-name">{student.name}</h4>
                            <div className="sp-badges">
                                {student.status ? <span className="sp-badge is-status">{student.status}</span> : null}
                                {student.grade ? <span className="sp-badge is-grade">{student.grade}</span> : null}
                                {student.rollNo ? <span className="sp-badge">Roll No. {student.rollNo}</span> : null}
                                {student.dob ? <span className="sp-badge">DOB: {student.dob}</span> : null}
                            </div>
                        </div>
                    </div>
                    <div className="sp-header-actions">
                        {/* Exam-board selector — drives every tab's report data.
                            Options are the boards the student actually has exams in
                            (getStudentExamStreams, keyed on
                            COALESCE(exam_paper.source_exam_id, exam_paper.exam_type)).
                            Shown only when there's a real choice to make. */}
                        {this.state.streamsLoaded && this.state.streamOptions.length > 1 && (
                            <div
                                className={`sp-stream-select ${this.state.streamMenuOpen ? 'open' : ''}`}
                                ref={(el) => { this.streamRef = el; }}
                            >
                                <span className="sp-stream-label">Exam</span>
                                <button
                                    type="button"
                                    className="sp-stream-trigger"
                                    onClick={this.toggleStreamMenu}
                                    aria-haspopup="listbox"
                                    aria-expanded={this.state.streamMenuOpen}
                                >
                                    <span className="sp-stream-trigger-ic"><i className="fas fa-graduation-cap" /></span>
                                    <span className="sp-stream-trigger-text">{selectedLabel}</span>
                                    {selectedCount > 0 ? (
                                        <span className="sp-stream-trigger-count">{selectedCount}</span>
                                    ) : null}
                                    <i className="fas fa-chevron-down sp-stream-caret" />
                                </button>
                                {this.state.streamMenuOpen && (
                                    <ul className="sp-stream-menu" role="listbox">
                                        {this.state.streamOptions.map((opt) => {
                                            const active = this.state.selectedKeys.includes(opt.value);
                                            return (
                                                <li key={opt.value} role="option" aria-selected={active}>
                                                    <button
                                                        type="button"
                                                        className={`sp-stream-item ${active ? 'active' : ''}`}
                                                        onClick={() => this.selectStream(opt.value)}
                                                    >
                                                        <span className="sp-stream-item-ic">
                                                            <i className={`${active ? 'fas fa-dot-circle' : 'far fa-circle'}`} />
                                                        </span>
                                                        <span className="sp-stream-item-text">{opt.label}</span>
                                                        {opt.count != null ? (
                                                            <span className="sp-stream-item-count">{opt.count} exams</span>
                                                        ) : null}
                                                        {active ? <i className="fas fa-check sp-stream-item-check" /> : null}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}
                        <button type="button" className="sp-back-btn" onClick={this.goBack}>
                            <i className="fas fa-arrow-left" /> Back
                        </button>
                    </div>
                </div>

                {/* ---- Tabs bar — grouped by MOU section so each requirement is
                     easy to find (Individual Analysis / Batch Analysis / Records). ---- */}
                <div className="sp-tabs-card">
                    <div className="sp-tabs-groups">
                        {GROUP_ORDER.map((groupName) => {
                            const groupTabs = TABS.filter((t) => t.group === groupName);
                            if (!groupTabs.length) return null;
                            return (
                                <div className="sp-tab-group" key={groupName}>
                                    <span className="sp-tab-group-label">{groupName}</span>
                                    <div className="sp-tab-group-items">
                                        {groupTabs.map((tab) => (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                className={`sp-tab-link ${activeTab === tab.key ? 'active' : ''}`}
                                                onClick={() => this.setTab(tab.key)}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ---- Active tab content (each tab is its own class component) ----
                    Held back until the board list resolves: examStreams starts from
                    the URL and may be empty (board ids are data, so there is no safe
                    default to guess), and mounting a tab before then would fire an
                    unfiltered query and then immediately refetch — a visible flash of
                    another board's numbers. */}
                {this.state.streamsLoaded
                    // Keyed on the selected exam: JEE Mains -> Quick Exam keeps the same
                    // board ids, so remounting is what makes every tab refetch.
                    ? <ActiveTab key={this.state.selectedKeys.join(KEY_SEP)} student={student} examStreams={this.state.examStreams} examKeys={this.state.selectedKeys} />
                    : <PageShimmer />}
            </div>
        );
    }
}

export default compose(withApollo, withRouter)(StudentProfileTabs);
